import { Trans } from '@lingui/react/macro'
import { Box, Group, Rating, Stack, Text } from '@mantine/core'
import { IconStarFilled } from '@tabler/icons-react'
import styles from './CsatChip.module.css'

interface Props {
  avgRating: number | null
  count: number
  /** Label shown above the score in the tile variant. */
  label?: React.ReactNode
  variant?: 'chip' | 'tile'
}

/**
 * CSAT display for the company portal. `chip` is a compact inline score for
 * property cards; `tile` mirrors the employee FeedbackStatsHero for the
 * portfolio hero and property page.
 */
export function CsatChip({ avgRating, count, label, variant = 'chip' }: Props) {
  const hasRatings = count > 0 && avgRating !== null

  if (variant === 'chip') {
    if (!hasRatings) {
      return (
        <Text size="sm" c="dimmed">
          <Trans>No ratings yet</Trans>
        </Text>
      )
    }
    return (
      <Group gap={4} wrap="nowrap">
        <IconStarFilled size={16} color="var(--mantine-color-brandOrange-6)" />
        <Text size="sm" fw={700} c="brandBrown.7">
          {avgRating.toFixed(1)}/5
        </Text>
        <Text size="xs" c="dimmed">
          ({count})
        </Text>
      </Group>
    )
  }

  return (
    <Box className={styles.badge}>
      <Stack className={styles.inner} gap={8}>
        {label && <Text className={styles.title}>{label}</Text>}
        {hasRatings ? (
          <>
            <Group gap="sm" align="center">
              <Text className={styles.score}>{avgRating.toFixed(1)}/5</Text>
              <Rating
                value={avgRating}
                fractions={10}
                readOnly
                color="brandOrange.6"
              />
            </Group>
            <Text size="xs" c="brandBrown.7">
              <Trans>{count} ratings · last 12 months</Trans>
            </Text>
          </>
        ) : (
          <>
            <Rating value={0} readOnly />
            <Text size="sm" c="brandBrown.7">
              <Trans>No ratings yet</Trans>
            </Text>
          </>
        )}
      </Stack>
    </Box>
  )
}
